import {Injectable} from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class AtlasUiDomEnhancerService {
  private readonly controlSelector: string = 'button, a.btn, a[role="button"], td a, th a, .ui-button, [role="button"], ' +
    '.ui-datepicker-prev, .ui-datepicker-next, .ui-timepicker a, .ui-paginator-element';
  private readonly countCandidateSelector: string = '[data-atlas-count], button, a, .btn';
  private readonly enhancedAttribute: string = 'data-atlas-tooltip-ready';
  private readonly selectionLabelSelector: string =
    '.ui-autocomplete-token-label, .ui-multiselect-token-label, .ui-chips-token-label';
  private readonly selectionTooltipAttribute: string = 'data-atlas-selection-tooltip-ready';
  private readonly filterCandidateSelector: string = 'form, .row';
  private readonly filterControlSelector: string = 'input:not([type="hidden"]):not([type="submit"])' +
    ':not([type="button"]):not([type="file"])' +
    ':not([type="checkbox"]):not([type="radio"]), ' +
    'select, .ui-dropdown, .ui-multiselect, .ui-autocomplete, .ui-calendar, p-dropdown, p-multiselect, p-autocomplete, p-calendar';
  private readonly filterTableSelector: string = 'table, p-table, .ui-table';
  private readonly filterExcludedContextSelector: string =
    '.modal, .ui-dialog, [role="dialog"], nav, header, .atlas-topbar, .atlas-sidebar';
  private readonly filterScopeSelector: string = '.panel, .card, .well, .widget, .portlet, .container-fluid, .tab-pane, section, main';

  attach(root: HTMLElement): () => void {
    if (!root) {
      return () => {};
    }

    this.enhanceTree(root);
    this.enhanceFilterPanels(root, root);

    const defaultView: Window = root.ownerDocument && root.ownerDocument.defaultView;
    const MutationObserverClass: typeof MutationObserver = defaultView && (defaultView as any).MutationObserver;
    if (!MutationObserverClass) {
      return () => {};
    }

    const observer: MutationObserver = new MutationObserverClass((records: MutationRecord[]) => {
      const addedElements: HTMLElement[] = [];
      records.forEach((record: MutationRecord) => {
        Array.prototype.slice.call(record.addedNodes).forEach((node: Node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            addedElements.push(node as HTMLElement);
          }
        });
      });

      addedElements
        .filter((element: HTMLElement, index: number, elements: HTMLElement[]) => {
          if (elements.indexOf(element) !== index) {
            return false;
          }
          return !elements.some((candidate: HTMLElement, candidateIndex: number) => {
            return candidateIndex !== index && candidate !== element && candidate.contains(element);
          });
        })
        .forEach((element: HTMLElement) => this.enhanceTree(element));

      this.refreshAffectedFilterScopes(root, addedElements);
    });

    observer.observe(root, {childList: true, subtree: true});
    return () => observer.disconnect();
  }

  private enhanceTree(root: HTMLElement): void {
    if (this.isCountCandidate(root)) {
      this.enhanceCountIndicator(root);
    }

    Array.prototype.slice.call(root.querySelectorAll(this.countCandidateSelector))
      .forEach((candidate: HTMLElement) => this.enhanceCountIndicator(candidate));

    if (this.isControl(root)) {
      this.enhanceControl(root);
    }

    Array.prototype.slice.call(root.querySelectorAll(this.controlSelector))
      .forEach((control: HTMLElement) => this.enhanceControl(control));

    this.enhanceSelectionLabels(root);
  }

  private enhanceSelectionLabels(root: HTMLElement): void {
    if (this.matchesAny(root, this.selectionLabelSelector)) {
      this.enhanceSelectionLabel(root);
    }

    Array.prototype.slice.call(root.querySelectorAll(this.selectionLabelSelector))
      .forEach((label: HTMLElement) => this.enhanceSelectionLabel(label));
  }

  private enhanceSelectionLabel(label: HTMLElement): void {
    if (label.hasAttribute(this.selectionTooltipAttribute)) {
      return;
    }

    const text: string = (label.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text) {
      return;
    }

    if (!(label.getAttribute('title') || '').trim()) {
      label.setAttribute('title', text);
    }
    label.setAttribute(this.selectionTooltipAttribute, 'true');
  }

  private refreshAffectedFilterScopes(root: HTMLElement, addedElements: HTMLElement[]): void {
    const scopes: HTMLElement[] = [];

    addedElements.forEach((element: HTMLElement) => {
      if (!this.affectsFilterClassification(element)) {
        return;
      }

      const scope: HTMLElement = this.findMutationFilterScope(element, root);
      if (scope && scopes.indexOf(scope) === -1) {
        scopes.push(scope);
      }
    });

    scopes.forEach((scope: HTMLElement) => this.enhanceFilterPanels(scope, root));
  }

  private affectsFilterClassification(element: HTMLElement): boolean {
    if (!element.matches) {
      return false;
    }

    if (this.matchesAny(element, this.filterCandidateSelector + ', ' + this.filterControlSelector + ', ' + this.filterTableSelector)) {
      return true;
    }

    return !!element.querySelector(this.filterCandidateSelector + ', ' + this.filterControlSelector + ', ' + this.filterTableSelector);
  }

  private findMutationFilterScope(element: HTMLElement, root: HTMLElement): HTMLElement {
    if (this.findAncestorMatching(element, this.filterTableSelector)) {
      return null;
    }

    let current: HTMLElement = element;
    while (current && current !== root) {
      if (this.isFilterScope(current)) {
        if (this.hasEligibleTable(current)) {
          return current;
        }
        if (this.isAppComponent(current)) {
          return null;
        }
      }
      current = current.parentElement;
    }

    return root && this.isFilterScope(root) && this.hasEligibleTable(root) ? root : null;
  }

  private enhanceFilterPanels(scanRoot: HTMLElement, boundary: HTMLElement): void {
    const candidates: HTMLElement[] = [];
    if (this.matchesAny(scanRoot, this.filterCandidateSelector)) {
      candidates.push(scanRoot);
    }

    Array.prototype.slice.call(scanRoot.querySelectorAll(this.filterCandidateSelector))
      .forEach((candidate: HTMLElement) => candidates.push(candidate));

    candidates.forEach((candidate: HTMLElement) => {
      if (this.isTableFilterPanel(candidate, boundary)) {
        candidate.classList.add('atlas-filter-panel');
      }
    });
  }

  private isTableFilterPanel(candidate: HTMLElement, boundary: HTMLElement): boolean {
    if (!candidate.matches || candidate.hasAttribute('data-atlas-ui-off') ||
      this.matchesAny(candidate, this.filterExcludedContextSelector) ||
      this.findAncestorMatching(candidate, this.filterExcludedContextSelector) ||
      this.isInsideTable(candidate) || candidate.querySelector(this.filterTableSelector) ||
      !candidate.querySelector(this.filterControlSelector)) {
      return false;
    }

    const isForm: boolean = candidate.tagName === 'FORM';
    if (!isForm && (this.findAncestor(candidate, ['FORM']) || candidate.querySelector('form') ||
      this.hasEligibleRowAncestor(candidate, boundary))) {
      return false;
    }

    return !!this.findFilterScopeWithFollowingTable(candidate, boundary);
  }

  private hasEligibleRowAncestor(candidate: HTMLElement, boundary: HTMLElement): boolean {
    let current: HTMLElement = candidate.parentElement;
    while (current && current !== boundary) {
      if (current.classList.contains('row') && !current.querySelector(this.filterTableSelector) &&
        !!current.querySelector(this.filterControlSelector)) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  }

  private findFilterScopeWithFollowingTable(candidate: HTMLElement, boundary: HTMLElement): HTMLElement {
    let current: HTMLElement = candidate.parentElement;
    while (current) {
      if ((current === boundary || this.isFilterScope(current)) && this.isBeforeFirstTable(candidate, current)) {
        return current;
      }
      if (this.isAppComponent(current)) {
        return null;
      }
      if (current === boundary) {
        break;
      }
      current = current.parentElement;
    }
    return null;
  }

  private isBeforeFirstTable(candidate: HTMLElement, scope: HTMLElement): boolean {
    const tables: HTMLElement[] = this.getEligibleTables(scope);
    if (!tables.length) {
      return false;
    }

    const firstTable: HTMLElement = tables[0];
    // tslint:disable-next-line:no-bitwise
    return !!(candidate.compareDocumentPosition(firstTable) & Node.DOCUMENT_POSITION_FOLLOWING);
  }

  private hasEligibleTable(scope: HTMLElement): boolean {
    return this.getEligibleTables(scope).length > 0;
  }

  private getEligibleTables(scope: HTMLElement): HTMLElement[] {
    return Array.prototype.slice.call(scope.querySelectorAll(this.filterTableSelector))
      .filter((table: HTMLElement) => {
        return !this.matchesAny(table, this.filterExcludedContextSelector) &&
          !this.findAncestorMatching(table, this.filterExcludedContextSelector);
      });
  }

  private isFilterScope(element: HTMLElement): boolean {
    return this.matchesAny(element, this.filterScopeSelector) || this.isAppComponent(element);
  }

  private isAppComponent(element: HTMLElement): boolean {
    const tagName: string = element.tagName || '';
    return tagName.indexOf('APP-') === 0;
  }

  private isInsideTable(element: HTMLElement): boolean {
    return this.matchesAny(element, this.filterTableSelector) || !!this.findAncestorMatching(element, this.filterTableSelector);
  }

  private matchesAny(element: HTMLElement, selector: string): boolean {
    return !!element.matches && element.matches(selector);
  }

  private findAncestorMatching(element: HTMLElement, selector: string): HTMLElement {
    let current: HTMLElement = element.parentElement;
    while (current) {
      if (this.matchesAny(current, selector)) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  private enhanceCountIndicator(candidate: HTMLElement): void {
    if (candidate.hasAttribute('data-atlas-ui-off') || !this.isCountIndicator(candidate)) {
      return;
    }

    candidate.classList.add('atlas-count-indicator', 'atlas-action-count');
    candidate.setAttribute('data-atlas-action', 'count');

    let icon: HTMLElement = candidate.querySelector('.atlas-count-icon, .fa-list-ol') as HTMLElement;
    if (!icon) {
      icon = candidate.ownerDocument.createElement('i');
      icon.setAttribute('aria-hidden', 'true');
      candidate.insertBefore(icon, candidate.firstChild);
    }

    icon.classList.add('fa', 'fa-list-ol', 'atlas-count-icon');
  }

  private isCountIndicator(candidate: HTMLElement): boolean {
    if (candidate.hasAttribute('data-atlas-count')) {
      return true;
    }

    const text: string = (candidate.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const recognizedCount: boolean = /^(?:total(?:\s*-\s*|\s+)count|total\s+item|toplam)(?:$|[^a-z0-9])/.test(text);
    const recognizedBtnTotal: boolean = candidate.classList.contains('btn') && /^total\s*:/.test(text);
    return recognizedCount || recognizedBtnTotal;
  }

  private enhanceControl(control: HTMLElement): void {
    if (control.hasAttribute('data-atlas-ui-off') || control.hasAttribute(this.enhancedAttribute)) {
      return;
    }

    const title: string = (control.getAttribute('title') || '').trim();
    const ariaLabel: string = (control.getAttribute('aria-label') || '').trim();
    const label: string = ariaLabel || title || this.resolveLabel(control);

    if (!title) {
      control.setAttribute('title', label);
    }
    if (!ariaLabel) {
      control.setAttribute('aria-label', label);
    }
    control.setAttribute(this.enhancedAttribute, 'true');
  }

  private resolveLabel(control: HTMLElement): string {
    const primeNgLabel: string = this.resolvePrimeNgLabel(control);
    if (primeNgLabel) {
      return primeNgLabel;
    }

    const text: string = (control.textContent || '').replace(/\s+/g, ' ').trim();
    if (text && text.toLowerCase() !== 'ui-btn' && text !== '×' && text !== '+' && text !== '-') {
      return text;
    }

    const labelledChild: HTMLElement = control.querySelector('[aria-label], [title]') as HTMLElement;
    const descendantLabel: string = labelledChild &&
      (labelledChild.getAttribute('aria-label') || labelledChild.getAttribute('title') || '').trim();
    if (descendantLabel) {
      return descendantLabel;
    }

    const signature: string = (control.className + ' ' + this.getIconClasses(control)).toLowerCase();
    if (signature.indexOf('calendar') !== -1) {
      return 'Open calendar';
    }
    if (signature.indexOf('caret-down') !== -1 || signature.indexOf('chevron-down') !== -1 ||
      signature.indexOf('dropdown') !== -1 || signature.indexOf('ellipsis') !== -1) {
      return 'Open options';
    }
    if (signature.indexOf('trash') !== -1 || signature.indexOf('delete') !== -1) {
      return 'Delete';
    }
    if (signature.indexOf('pencil') !== -1 || signature.indexOf('edit') !== -1) {
      return 'Edit';
    }
    if (signature.indexOf('info') !== -1) {
      return 'View details';
    }
    if (signature.indexOf('eye') !== -1) {
      return 'View';
    }
    if (signature.indexOf('plus') !== -1) {
      return 'Add';
    }
    if (signature.indexOf('search') !== -1) {
      return 'Search';
    }
    if (signature.indexOf('refresh') !== -1 || signature.indexOf('repeat') !== -1) {
      return 'Refresh';
    }
    if (signature.indexOf('download') !== -1 || signature.indexOf('file-excel') !== -1) {
      return 'Download';
    }
    if (signature.indexOf('upload') !== -1) {
      return 'Upload';
    }
    if (signature.indexOf('save') !== -1 || signature.indexOf('check') !== -1) {
      return 'Save';
    }
    if (signature.indexOf('list') !== -1) {
      return 'View list';
    }
    if (signature.indexOf('cog') !== -1 || signature.indexOf('gear') !== -1) {
      return 'Open actions';
    }
    if (signature.indexOf('sort') !== -1) {
      return 'Change order';
    }
    if (signature.indexOf('times') !== -1 || signature.indexOf('close') !== -1) {
      return 'Close';
    }
    if (control.getAttribute('data-toggle') === 'modal') {
      return 'Open dialog';
    }
    if (control.getAttribute('data-toggle') === 'collapse') {
      return 'Toggle section';
    }
    if (this.findAncestor(control, ['TD', 'TH'])) {
      return 'Open row action';
    }
    return 'Activate control';
  }

  private resolvePrimeNgLabel(control: HTMLElement): string {
    if (control.classList.contains('ui-datepicker-prev')) {
      return 'Previous month';
    }
    if (control.classList.contains('ui-datepicker-next')) {
      return 'Next month';
    }
    if (control.classList.contains('ui-paginator-first')) {
      return 'First page';
    }
    if (control.classList.contains('ui-paginator-prev')) {
      return 'Previous page';
    }
    if (control.classList.contains('ui-paginator-next')) {
      return 'Next page';
    }
    if (control.classList.contains('ui-paginator-last')) {
      return 'Last page';
    }
    if (control.classList.contains('ui-paginator-page')) {
      const page: string = (control.textContent || '').trim();
      return page ? 'Page ' + page : 'Page';
    }
    if (control.classList.contains('ui-paginator-element')) {
      return 'Page navigation';
    }

    const timePicker: HTMLElement = this.findAncestorByClass(control, 'ui-timepicker');
    if (!timePicker) {
      return '';
    }

    const signature: string = this.getIconClasses(control).toLowerCase();
    const parentClass: string = control.parentElement ? control.parentElement.className.toLowerCase() : '';
    let unit = 'time';
    if (parentClass.indexOf('ui-hour-picker') !== -1) {
      unit = 'hour';
    } else if (parentClass.indexOf('ui-minute-picker') !== -1) {
      unit = 'minute';
    } else if (parentClass.indexOf('ui-second-picker') !== -1) {
      unit = 'second';
    }

    if (signature.indexOf('chevron-up') !== -1) {
      return 'Increase ' + unit;
    }
    if (signature.indexOf('chevron-down') !== -1) {
      return 'Decrease ' + unit;
    }
    return 'Adjust time';
  }

  private getIconClasses(control: HTMLElement): string {
    const icon: HTMLElement = control.querySelector('.fa, .pi, .glyphicon, .material-icons, .ui-button-icon-left') as HTMLElement;
    return icon ? icon.className : '';
  }

  private isControl(element: HTMLElement): boolean {
    if (!element.matches) {
      return false;
    }

    return element.matches('button, a.btn, a[role="button"], .ui-button, [role="button"], ' +
      '.ui-datepicker-prev, .ui-datepicker-next, .ui-timepicker a, .ui-paginator-element') ||
      (element.tagName === 'A' && !!this.findAncestor(element, ['TD', 'TH']));
  }

  private isCountCandidate(element: HTMLElement): boolean {
    return !!element.matches && element.matches(this.countCandidateSelector);
  }

  private findAncestorByClass(element: HTMLElement, className: string): HTMLElement {
    let current: HTMLElement = element.parentElement;
    while (current) {
      if (current.classList.contains(className)) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  private findAncestor(element: HTMLElement, tagNames: string[]): HTMLElement {
    let current: HTMLElement = element.parentElement;
    while (current) {
      if (tagNames.indexOf(current.tagName) !== -1) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }
}
